import { useCallback, useEffect, useRef } from 'react'

import { useAppContext, useAppDispatch, useAppStore } from '@/hooks'
import { useBulkLockMutation } from '@/rtkApi/mddApiV2'
import { loadNodeChildren } from '@/store/modules/node/actions'

import { useSelectedBranchNumericId } from '../../../TreePanelContext'

import { useBatchEditContext } from '../context/BatchEditContext'
import { ActionTypeEnum } from '../context/types'

export const useLocking = () => {
	const { t } = useAppContext()
	const dispatch = useAppDispatch()
	const loggedUserId = useAppStore((state) => state.auth.user?.id)
	const refLocked = useRef(false)

	const [bulkLock] = useBulkLockMutation()
	const branchId = useSelectedBranchNumericId()

	const {
		state: { selectedStructures, locking, errors, skipLockCheck },
		dispatch: dispatchContext,
	} = useBatchEditContext()

	const lockNodes = useCallback(async () => {
		const nodesToLock: number[] = []
		const parentIds = new Set<number>()

		for (const node of selectedStructures) {
			// Skip lock check if we just force unlocked these nodes
			if (!skipLockCheck && node.lockTime && node.lockUserId !== loggedUserId) {
				dispatchContext({
					type: ActionTypeEnum.SET,
					state: {
						errors: {
							...errors,
							[node.id]: `${t('ELEMENT')} ${node.name} ${t('LOCKED_BY_USER')}`,
						},
					},
				})
				continue
			}

			// Lock nodes that aren't already locked (or were just unlocked)
			if ((!node.lockTime || skipLockCheck) && node.id) {
				nodesToLock.push(node.id)
				if (node.parentStructureId) {
					parentIds.add(node.parentStructureId)
				}
			}
		}

		if (nodesToLock.length > 0 && branchId != null) {
			await bulkLock({ branchId, id: nodesToLock }).unwrap()
		}

		for (const parentId of parentIds) {
			dispatch(loadNodeChildren(parentId))
		}

		refLocked.current = true
		dispatchContext({ type: ActionTypeEnum.SET, state: { locking: false } })
	}, [
		branchId,
		bulkLock,
		dispatch,
		dispatchContext,
		errors,
		loggedUserId,
		selectedStructures,
		skipLockCheck,
		t,
	])

	useEffect(() => {
		if (locking || refLocked.current) {
			return
		}

		dispatchContext({ type: ActionTypeEnum.SET, state: { locking: true } })

		lockNodes()
	}, [dispatch, dispatchContext, lockNodes, locking, selectedStructures])
}
